// systems/tomeOfEchoes.js
// Tome of Echoes - tracks all items ever obtained by the player

import { gameState } from '../game/gameState.js';
import { getAllItems, getItemsByCategory, ITEM_CATEGORIES, getItem, getItemIcon } from '../data/items/index.js';

/**
 * Category display order for the Tome UI
 * Organized by gameplay progression: Gathering -> Processing -> Crafting -> Equipment
 * Categories can have multiple IDs merged together using `ids: [...]`
 */
export const TOME_CATEGORY_ORDER = [
    // Gathering
    { id: ITEM_CATEGORIES.ORE, name: 'Ores', icon: '<span class="mining-icon"></span>' },
    { id: ITEM_CATEGORIES.LOG, name: 'Logs', icon: '🪵' },
    { id: ITEM_CATEGORIES.FISH_RAW, name: 'Raw Fish', icon: '🐟' },
    { id: ITEM_CATEGORIES.CROP, name: 'Crops', icon: '🌾' },
    { id: ITEM_CATEGORIES.HIDE, name: 'Hides', icon: '🦴' },

    // Processing
    { id: ITEM_CATEGORIES.BAR, name: 'Bars', icon: '🔩' },
    { id: 'food', ids: [ITEM_CATEGORIES.FISH_COOKED, ITEM_CATEGORIES.FISH_EMPOWERED], name: 'Food', icon: '🍣' },

    // Seeds (consolidated)
    { id: 'seeds', ids: [ITEM_CATEGORIES.SEED, ITEM_CATEGORIES.TREE_SEED, ITEM_CATEGORIES.HERB_SEED], name: 'Seeds', icon: '🌱' },

    // Herbs (consolidated)
    { id: 'herbs', ids: [ITEM_CATEGORIES.HERB_GRIMY, ITEM_CATEGORIES.HERB_CLEAN], name: 'Herbs', icon: '🌿' },

    // Alchemy (vials merged into potions)
    { id: 'alchemy', ids: [ITEM_CATEGORIES.VIAL, ITEM_CATEGORIES.POTION], name: 'Alchemy', icon: '⚗️' },

    // Ammunition (arrow components)
    { id: 'ammunition', ids: [ITEM_CATEGORIES.ARROWHEAD, ITEM_CATEGORIES.ARROW_SHAFT, ITEM_CATEGORIES.ARROW], name: 'Ammunition', icon: '🏹' },

    // Glyphbinding (consolidated)
    { id: 'glyphbinding', ids: [ITEM_CATEGORIES.ICHOR, ITEM_CATEGORIES.GLYPH, ITEM_CATEGORIES.GLYPH_CORE], name: 'Glyphbinding', icon: '✨' },

    // Materials
    { id: ITEM_CATEGORIES.GEM, name: 'Gems', icon: '💎' },
    { id: ITEM_CATEGORIES.BONE, name: 'Bones', icon: '🦴' },
    { id: ITEM_CATEGORIES.CLOTH, name: 'Cloth', icon: '🧵' },

    // Jewelry (consolidated)
    { id: 'jewelry', ids: [ITEM_CATEGORIES.BAND, ITEM_CATEGORIES.PENDANT, ITEM_CATEGORIES.COMBAT_PENDANT], name: 'Jewelry', icon: '💍' },

    // Equipment
    { id: ITEM_CATEGORIES.ARMOR, name: 'Armor', icon: '🛡️' },
    { id: 'weapons', ids: [ITEM_CATEGORIES.WEAPON, ITEM_CATEGORIES.BOW], name: 'Weapons', icon: '⚔️' },
    { id: 'offhands', ids: [ITEM_CATEGORIES.BULWARK, ITEM_CATEGORIES.FOCUS, ITEM_CATEGORIES.QUIVER], name: 'Offhands', icon: '🔰' },

    // Boss & Set Equipment
    { id: ITEM_CATEGORIES.BOSS_GEAR, name: 'Boss Equipment', icon: '🔥' },
    { id: ITEM_CATEGORIES.SET_ARMOR, name: 'Set Armor', icon: '⚙️' },

    // Pets
    { id: 'pets', ids: [ITEM_CATEGORIES.PET_FOOD, ITEM_CATEGORIES.PET_MATERIAL], name: 'Pets', icon: '🐾' },

    // Special
    { id: ITEM_CATEGORIES.CURRENCY, name: 'Currency', icon: getItemIcon('coins') },
    { id: ITEM_CATEGORIES.MISC, name: 'Misc', icon: '📦' },
];

/**
 * Check if an item has ever been discovered
 * @param {string} itemId - The item identifier
 * @returns {boolean}
 */
export function isDiscovered(itemId) {
    return gameState.isItemDiscovered(itemId);
}

/**
 * Get total count of a specific item ever obtained
 * @param {string} itemId - The item identifier
 * @returns {number}
 */
export function getItemObtainedCount(itemId) {
    return gameState.getTomeItemCount(itemId);
}

/**
 * Get all discovered item IDs
 * @returns {string[]}
 */
export function getDiscoveredItems() {
    const tome = gameState.getTomeOfEchoes();
    // Filter out ghost items: IDs still in the save's itemCounts but removed from
    // the registry (deleted or renamed items). Without this, getDiscoveryStats()
    // counts ghosts as discovered while total only counts live items -> >100%.
    return Object.keys(tome.itemCounts).filter(id => tome.itemCounts[id] > 0 && getItem(id) !== null);
}

/**
 * Get total number of unique items discovered
 * @returns {number}
 */
export function getTotalDiscovered() {
    return getDiscoveredItems().length;
}

/**
 * Get overall discovery statistics
 * @returns {{ total: number, discovered: number, percentage: number }}
 */
export function getDiscoveryStats() {
    const allItems = getAllItems();
    const total = allItems.length;
    const discovered = getTotalDiscovered();
    const percentage = total > 0 ? Math.round((discovered / total) * 100) : 0;

    return { total, discovered, percentage };
}

/**
 * Get statistics for a specific category
 * Supports merged categories where categoryInfo has `ids: [...]` array
 * @param {string} categoryId - The category ID from TOME_CATEGORY_ORDER
 * @returns {{ id: string, name: string, icon: string, total: number, discovered: number, percentage: number, items: Array }}
 */
export function getCategoryStats(categoryId) {
    const categoryInfo = TOME_CATEGORY_ORDER.find(c => c.id === categoryId);
    if (!categoryInfo) {
        return null;
    }

    // Get items from all merged categories or single category
    const categoryIds = categoryInfo.ids || [categoryId];
    let items = [];
    for (const catId of categoryIds) {
        items = items.concat(getItemsByCategory(catId));
    }

    const total = items.length;
    let discovered = 0;

    const itemsWithStatus = items.map(item => {
        const obtainedCount = getItemObtainedCount(item.id);
        const isItemDiscovered = obtainedCount > 0;
        if (isItemDiscovered) discovered++;

        return {
            ...item,
            discovered: isItemDiscovered,
            obtainedCount
        };
    });

    const percentage = total > 0 ? Math.round((discovered / total) * 100) : 0;

    return {
        id: categoryId,
        name: categoryInfo.name,
        icon: categoryInfo.icon,
        total,
        discovered,
        percentage,
        items: itemsWithStatus
    };
}

/**
 * Get statistics for all categories
 * @returns {Array} Array of category stats, filtered to only include categories with items
 */
export function getAllCategoryStats() {
    const stats = [];

    for (const category of TOME_CATEGORY_ORDER) {
        const categoryStats = getCategoryStats(category.id);
        if (categoryStats && categoryStats.total > 0) {
            stats.push(categoryStats);
        }
    }

    return stats;
}

/**
 * Search items by name
 * @param {string} query - Search query
 * @returns {Array} Array of items matching the query with discovery status
 */
export function searchItems(query) {
    if (!query || query.trim().length === 0) {
        return [];
    }

    const lowerQuery = query.toLowerCase().trim();
    const allItems = getAllItems();

    return allItems
        .filter(item => item.name.toLowerCase().includes(lowerQuery))
        .map(item => {
            const obtainedCount = getItemObtainedCount(item.id);
            return {
                ...item,
                discovered: obtainedCount > 0,
                obtainedCount
            };
        });
}

/**
 * Get completion percentage for a category
 * @param {string} categoryId - The category ID
 * @returns {number} Percentage (0-100)
 */
export function getCategoryCompletion(categoryId) {
    const stats = getCategoryStats(categoryId);
    return stats ? stats.percentage : 0;
}

/**
 * Check if a category is fully completed
 * @param {string} categoryId - The category ID
 * @returns {boolean}
 */
export function isCategoryComplete(categoryId) {
    const stats = getCategoryStats(categoryId);
    return stats ? stats.discovered === stats.total : false;
}

export default {
    TOME_CATEGORY_ORDER,
    isDiscovered,
    getItemObtainedCount,
    getDiscoveredItems,
    getTotalDiscovered,
    getDiscoveryStats,
    getCategoryStats,
    getAllCategoryStats,
    searchItems,
    getCategoryCompletion,
    isCategoryComplete
};
